import { DbOrganisationTypeIds, RateTypeIds, ResolutionTypeIds, DbQueueProcessingStatusId, DbQueueOperationResultStatusId, DbServerType } from "./shared-enums";
import { IdName, IArchivedAndSymbol } from "./interfaces";
import { BaseDto } from "../models/base-dto";

export interface ISqafFunctionPermission {
	fpId: number;
	fpSymbol: string;
	fpName: string;
	fpFptId: number;
	fpDescription: string;
	fpClsId: number;
	fpPcId: number;
}

export interface IDbDocumentSet extends IdName {
	id: number;
	name: string;
	symbol: string;
	isAccReceivable: boolean;
	isAccPayable: boolean;
}

export interface IDbPlzTargetingCheckResult {
	postalCode: string;
	newValue: string;
	isValid: boolean;
}

export interface IDbConnectionInformation {
	name: string;
	webServer: string;
	datasource: string;
	catalog: string;
	catalogVersion: string;
	user: string;
	marketer: string;
	marketerKey: string;
	backup: string;
	backupDate: Date;
	isTestEnvironment: boolean;
	colorValue1: number;
	colorValue2: number;
	color1: string;
	color2: string;
}
export interface IObjectLinkResult {
	title: string;
	path: string;
	isPrimary: boolean;
	isRelative: boolean;
	category: number;
}

export interface IDbPriceType extends IdName {
	symbol: string;
	factor: number;
	unit: string;
	modifier: string;
	perUnit: string;
	docUnit: string;
	isCpm: boolean;
	isAutoDelivery: boolean;
	sortId: number;
	isArchived: boolean;
	nameEnu: string;
	unitEnu: string;
	perUnitEnu: string;
	docUnitEnu: string;
	detailName: string;
	showInInventoryPackageItem: boolean;
}

export interface IDbPriceTypeDetail extends IdName {
	isArchived: boolean;
	ptId: number;
}

export interface IDbUserAccount extends IdName {
	titId: number;
	prename: string;
	surname: string;
	uastId: number;
	remark: string;
	uatId: number;
	employeeId: string;
	templateUaId: number;
	crmNumber: string;
	rowversion: number[];
}

export class DbUserNotification {
	public utId?: number;
	public utResponsibleUaId?: number;
	public utCreatedUaId?: number;
	public utCreatedTime?: Date;
	public utCutoffDate?: Date;
	public utClsId?: number;
	public utObjId?: number;
	public utObjTitle?: string;
	public utInstruction?: string;
	public utSubClsId?: number;
	public utSubObjId?: number;
	public utFinishFromDate?: Date;
	public utFinishToDate?: Date;
	public utFinishedTime?: Date;
	public utFinishedRemark?: Date;
	public utTitle?: string;
	public utUtcId?: number;
	public utCndId?: number;
	public utUserTaskConfig?: IDbUserTaskConfig;
}

export class BaseToTimeTargetingDto extends BaseDto {
	public monday: number;
	public tuesday: number;
	public wednesday: number;
	public thursday: number;
	public friday: number;
	public saturday: number;
	public sunday: number;
}

export interface IDbAdServerTargetingKeys extends IdName {
	siId: number;
	displayName: string;
	sortId: number;
	isArchived: boolean;
	keyId: number;
	tkgId: number;
}

export interface IDbAdServerTargetingValues extends IdName {
	keyId: number;
	isArchived: boolean;
	parentId: number;
}

export interface IDbAdServerGroupedTargetingValues extends IdName {
	keyId: number;
	keyName: string;
	siId: number;
	siName: string;
	setId: number;
	group: string;
	subGroup: string;
	isArchived: boolean;
	sortId: number;
}

export interface IDbUserTask {
	id?: number;
	responsibleUaId?: number;
	createdUaId?: number;
	createdTime?: Date;
	cutoffDate?: Date;
	clsId?: number;
	objId?: number;
	objTitle?: string;
	instruction?: string;
	subClsId?: number;
	subObjId?: number;
	finishFromDate?: Date;
	finishToDate?: Date;
	finishedTime?: Date;
	finishedRemark?: Date;
	title?: string;
	utcId?: number;
	userTaskConfig?: IDbUserTaskConfig;
}

export interface IDbUserTaskConfig {
	id: number;
	name: string;
	automatic: boolean;
	closeManually: boolean;
}

export interface IUserNotificationsParameter {
	responsibleUserAccountId?: number;
	maxCutoffDateOffset: number;
	includeFinishedTasks: boolean;
	lastUserTaskId?: number;
}

export interface IDbAdvertisingMediaStateProperty {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbCampaignPropertyIntern extends IdName {
	symbol: string;
	archived: boolean;
	showInPriceList: boolean;
}

export interface IDbCampaignPropertyIntern extends IdName, IArchivedAndSymbol { }

export interface IDbInventoryEnhancement extends IdName, IArchivedAndSymbol {
	sortId: number;
}

export interface IDbSiteProperty extends IdName, IArchivedAndSymbol { }

export interface IDbWeekDay extends IdName {
	symbol: string;
	weekday: number;
}

// Werbeform - Tarifgruppen
export type IDbAdvertisingTypePriceGroup = IdName;

export interface IDbPriceListProperty extends IdName {
	symbol: string;
	description: string;
	labelPosition?: number;
	sortId: number;
	combinablePlpIdSet: number;
}

export type IDbPriceList = IdName;

export interface IDbCommercialSiteToPriceList extends IdName {
	plId: number; // PriceList
	csId: number;
}

export interface IDbCampaignLineItemProperty extends IdName {
	symbol: string;
	fpId: number;
	showInPriceList: boolean;
}

export interface IDbMarketer extends IdName {
	orgId: number;
	orgTypeId: number;
	crId: number;
	isArchived: boolean;
	isInUse: boolean;
	state: number;
	mktId: number;
}

// Werbeform
export interface IDbAdvertisingType extends IdName {
	matchcode: string;
	ptIdSet?: number;
	atpgId?: number;
	asttId?: number;
	ivcId?: number;
	atgId?: number;
	atcId?: number;
	stIdSet?: number;
	atpIdSet?: number;
	deliveryDeadline?: number;
	iconFlId?: number;
	specificationFlId?: number;
	archived: boolean;
	ssosId?: number;
	minPrice?: number;
	crmNumber?: string;
	sysId?: number;
}

// Inventory
export interface IDbInventoryItem extends IdName {
	parentIvId: number;
	matchcode: string;
	nameEnu: string;
	ivcId: number;
	csId: number;
	description: string;
	archived: boolean;
	ivpIdSet: number;
	specificationFlId?: number;
	ivtId: number;
	ssosId: number;
	pltId?: number;
	ifId?: number;
	ieIdSet: number;
}

export interface IRemarkVariable extends IdName {
	description: string;
	resolution: any;
}

export interface IDbCurrency extends IdName {
	nameIso: string;
}

export interface IDbCommercialSite extends IdName {
	archived: boolean;
	siId: number;
}

// Inventar-Tarifgruppen
export interface IDbPriceGroup extends IdName {
	sortId?: number;
	archived: boolean;
}

export interface IDbCampaignInstallment extends IdName {
	cigId: number;
	perfintFrom: Date;
	perfintTo: Date;
	cutoffDate: Date;
	perfextFrom: Date;
	perfextTo: Date;
	carId: number;
	invDocId: number;
	isChecked: boolean;
}

export interface IDbCampaignToDataFile extends IdName {
	cpgId: number;
	udtId: number;
	flId: number;
	remark: string;
}

export interface IDbOrganisation {
	organisationProperty: number;
	organisationType: number;
	id: number;
	otId: DbOrganisationTypeIds;
	mktId: number;
	number: string;
	crmNumber: string;
	name: string;
	shortName: string;
	validFrom: Date;
	validTo: Date;
	opIdSet: number;
	oipIdSet: number;
	remark: string;
	nameExtension: string;
	website: string;
	stdId: string;
	stdArea: string;
	stdName: string;
	accountingNumber: string;
	sofId: number;
	caId: number;
	pymId: number;
	pytId: number;
	lngId: number;
	crId: number;
	nationalTaxId: string;
	internationalTaxId: string;
	documentRecipient: string;
	documentCcRecipients: string;
	accId: number;
	isLocked: boolean;
	isProspect: boolean;
	isBookingCustomer: boolean;
	calculateSalesTax: boolean;
	referenceId1: string;
	referenceId2: string;
	referenceId3: string;
	sysId: number;
	ddoId: number;
	maftId: number;
	mafsId: number;
	mafcId: number;
	lockReason: string;
	archived: boolean;
	saId: number;
	rowversion: number[];
}

export class DbDiscountAgreement {
	public discountAgreementProperty: number;
	public paymentTerm: number;
	public id: number;
	public dapIdSet: number;
	public name: string;
	public discountYear: number;
	public validFrom: Date;
	public validTo: Date;
	public pytId: number;
	public pytIsOverwritable: boolean;
	public pypId: number;
	public pypIsOverwritable: boolean;
	public hasVolumeDiscount: boolean;
	public specialDiscountCount: number;
	public remark: string;
	public archived: boolean;
	public referenceDaId: number;
	public discountNames: string;
	public sdaId: number;
	public rowversion: number[];
	public ownerId: number;
	public ownerOtId: number;
	public ownerName: string;
	public orgIds: number[];
}

export interface ICampaignVersions extends IdName {
	version: number;
	cpgsId: number;
	isActive: boolean;
	isCurrent: boolean;
}

export interface IDbDiscountAgreementProperty extends IdName {
	id: number;
	symbol: string;
	name: string;
	isArchived: boolean;
}

export interface IDbSalesTax extends IdName {
	id: number;
	mktIdSet: number;
	validFrom: Date;
	validTo: Date;
	rate: number;
}

export class DbDocumentConfiguration {
	public language: number;
	public organisationByMdtId: IDbOrganisation;
	public organisationByMktId: IDbOrganisation;
	public id: number;
	public version: number;
	public mktId: number;
	public marketerSpecific: boolean;
	public mdtId: number;
	public validFrom: Date;
	public validTo: Date;
	public dtId: number;
	public lngId: number;
	public address: string;
	public replacesBasicCfg: boolean;
	public logoFlId: number;
	public textModule1: string;
	public textModule2: string;
	public isArchived: boolean;
	public rowversion: number[];
}

export interface IDbMailTemplateType extends IdName {
	id: number;
	symbol: string;
	name: string;
	profile: string;
}

export interface IDbLanguage extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbRemarkText extends IdName {
	rtId: number;
	rtName: string;
	rtText: string;
	rtClsId: number;
	rtIsArchived: boolean;
	rtRowversion: string;
	clitId: number;
	rtLngId: number;
	rtMktIdSet: number;
}

export interface IDbDocumentProperty {
	id: number;
	symbol: string;
	name: string;
	dtIdSet: number;
}

export interface IDbDocumentType extends IdName {
	id: number;
	symbol: string;
	name: string;
	nameEnu: string;
	documentGroup: string;
	sortid: number;
	isSelectable: boolean;
	isActive: boolean;
}
export interface IDbDocumentDeliveryOption extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbVolumeOrigin extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbDiscountGradation extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbDiscountCalculationProcedure extends IdName {
	id: number;
	symbol: string;
	name: string;
	isArchived: boolean;
}

export interface IDbPaymentTerm extends IdName {
	id: number;
	symbol: string;
	name: string;
	documentText: string;
	timeForPayment: number;
	cashDiscountPrc: number;
	timeForDiscount: number;
	overdueDemandNote: boolean;
	ledgerCode: string;
	documentTextEnu: string;
	isArchived: boolean;
	isForInvoicing: boolean;
	isForVoucher: boolean;
	mktIdSet: number;
}

export interface IDbPaymentPlan extends IdName {
	id: number;
	symbol: string;
	name: string;
	actualValue: boolean;
	isAutomatic: boolean;
	piId: number;
	intervalCount: number;
	referenceOffset: number;
	isPrepayment: boolean;
	archived: boolean;
	disabled: boolean;
}

export interface IDbOrganisationProperty extends IdName {
	id: number;
	symbol: string;
	otIdSet: number;
	name: string;
	isArchived: boolean;
}

export interface IDbDiscountGroup extends IdName {
	id: number;
	daId: number;
	dgtId: number;
	name: string;
	isDefault: boolean;
}

export interface IDbOrganisationType extends IdName {
	id: DbOrganisationTypeIds;
	symbol: string;
	name: string;
	masterOtId: number;
	icoId: number;
}

export interface IDbSubtotalType extends IdName {
	sttpIdSet: number; // DbSubtotalTypePropertyIds;
}

export interface IDbSystem extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbTitle extends IdName {
	id: number;
	symbol: string;
	name: string;
	nameEnu: string;
	isDefault: boolean;
	isGroup: boolean;
	salutation: string;
}

export interface IDbSepaMandateStatus extends IdName {
	id: number;
	symbol: string;
	name: string;
}
export interface IDbPaymentMethods extends IdName {
	id: number;
	symbol: string;
	name: string;
	description: string;
}
export interface IDbMailAttachmentFileSize extends IdName {
	id: number;
	symbol: string;
	name: string;
}
export interface IDbMailAttachmentFileType extends IdName {
	id: number;
	symbol: string;
	name: string;
}
export interface IDbMailAttachmentFileCount extends IdName {
	id: number;
	symbol: string;
	name: string;
}
export interface IDbCreditApproval extends IdName {
	id: number;
	symbol: string;
	name: string;
	description: string;
}
export interface IDbPersonProperty extends IdName {
	id: number;
	symbol: string;
	name: string;
	unique: boolean;
}

export interface IDbAddressProperty extends IdName {
	id: number;
	symbol: string;
	name: string;
	unique: boolean;
}
export interface IDbAccountingClassification extends IdName {
	id: number;
	symbol: string;
	name: string;
}

export interface IDbCountries extends IdName {
	id: number;
	symbol: string;
	name: string;
	unique: boolean;
}

export interface IDbAgencyContract extends IdName {
	validFrom: Date;
	validTo: Date;
	name: string;
	flId: number;
	volumeSttId: number; // Umsatzbasis
	expectedVolumeSc: number; // Umsatzerwartung
	commissionPrc: number; // Rückvergütung
	revenueReductionPrc: number;
	serviceFeePrc: number;
	excludedPtIdSet: number; // Ausgenommene Preistypen
}

export interface IDbServerInstance extends IdName {
	id: number;
	name: string;
	stId: number;
	typeName: string;
	orgId: number;
	sipIdSet: number;
	isArchived: boolean;
}

export interface IDbSalesOffice extends IdName {
	id: number;
	name: string;
	mktId: number;
	sofpIdSet: number;
}

export interface IDbUserRole {
	id: number;
	symbol: string;
	name: string;
	remark: string;
	assignable: boolean;
}

export interface IDbClassToUserRoleInfo {
	id: number;
	clsId: number;
	clsName: string;
	urId: number;
	name: string;
	required: boolean;
	requiredMktIdSet: number;
	delimitable: boolean;
	urName: string;
}

export interface IDbSqafDataFileHeader {
	id: number;
	name: string;
	mimeType: string;
	isTemporary: boolean;
	contentLength: number;
}

export class FunctionPermissionDto {
	public id: number;
	public name: string;
	public description: string;
	public granted: boolean;
}

export class SqafDataFileHeaderDto extends BaseDto {
	public name: string;
	public mimeType: string;
	public isTemporary: boolean;
	public contentLength: number;
}

export class SendMailDto extends BaseDto {
	public recipient: string;
	public subject: string;
	public attachements: string;
	public contentId: number;
	public content: string;
	public hasChanged: boolean;
	public ccRecipient: string;
	public docIdSet: string;
	public cpgId: number;
	public mtplId: number;
	public mtplName: string;
}

export interface IDbMailTemplate extends IdName {
	mtplId: number;
	mtplName: string;
	mtplSubject: string;
	mtplContent: string;
	mtplLngId: number;
	mtplMtpltId: number;
	mtplFooter: string;
	mtplLogoFlId: number;
	mtplValidFrom: Date;
	mtplValidTo: Date;
	mtplArchived: boolean;
	mtplVersion: number;
}

export interface IDbLedgerExport extends IdName {
	lexDirectory: string;
	lexId: number;
	lexName: string;
	lexSymbol: string;
	lexSortId: number;
	lexIsArchived: boolean;
}

export interface IPaymentTerm extends IdName {
	symbol: string;
	documentText: string;
}

export interface IDbMandatorContractDocumentOption {
	id: number;
	name: string;
	symbol: string;
	isArchived: boolean;
}

export interface IPublisherContractCommissionScaleType extends IdName {
	symbol: string;
}

export interface IDbMandatorContractProperties extends IdName {
	symbol: string;
}

export interface IDocumentShipment extends IdName {
	isArchived: boolean;
	icoId: number;
}

export interface IDbBusinessSegments extends IdName {
	bspIdSet: number;
	isArchived: boolean;
	abstract: boolean;
	archived: boolean;
}

export interface IDbMandatorContractExclusiveness extends IdName {
	symbol: string;
}

export interface IDbMandatorContractStatus extends IdName {
	symbol: string;
}

export interface IDbSellingChannel extends IdName {
	isArchived: boolean;
}

export interface IDbPeriodType extends IdName {
	symbol: string;
}

export interface IDbMandatorContractCommissionScaleType extends IdName {
	symbol: string;
}

export interface IDbMandatorContractRevenueClass extends IdName {
	symbol: string;
}

export interface IPublisherContractStatus extends IdName {
	symbol: string;
	name: string;
}

export interface IPublisherContractCalculationProcedure extends IdName {
	symbol: string;
	isArchived: boolean;
}

export class DeliveryStatusDto extends BaseDto {
	public dateFrom: string;
	public dateTo: string;
	public targetUnits: number;
	public deliveredUnits: number;
	public priceTypeUnit: string;
}

export interface IAvailabilityStatusDto extends BaseDto {
	stId: DbServerType,
	autoAvailableUnits: number;
	dateFrom: string;
	dateTo: string;
	manAvailableUnits: number;
	priceTypeUnit: string;
	targetUnits: number;
	lifcMatchedUnits: number;
	lifcUnavailable: number;
	lifcReservedUnits: number;
	lifcAvailableUnits: number;
	reductionPrc: number;
	lifcReducedAvailable: number;
	lifcRequestedDate: string;
}

export class ResponsibilityDto extends BaseDto {
	public uaId: number;
	public objId: number;
	public urId: number;
	public validFrom: Date;
	public validTo: Date;
	public clsturId: number;
	public mktIdSet: number;
}

export class ResponsibilityUserAccountDto extends BaseDto {
	public name: string;
}

export class RemarkDto extends BaseDto {
	public objId: number;
	public clsId: number;
	public uaId: number;
	public uaName: string;
	public remcIdSet: number;
	public rempIdSet: number;
	public lngId: number;
	public text: string;
	public sortId: number;
	public mktId: number;
	public creationDate: Date;
}

export interface IDbRemarkContext extends IdName {
	id: number;
	symbol: string;
	name: string;
	fpId: number;
	clsId: number;
	cpgtIdSet: number;
	stId?: number;
	isArchived: boolean;
	disabled: boolean; //mapped to isReadOnly
}

export interface IDbServerType {
	id: number;
	symbol: string;
	name: string;
	icoId: number;
}

export interface IDbTimeTargetingQuickSelect extends IdName {
	monday: number;
	tuesday: number;
	wednesday: number;
	thursday: number;
	friday: number;
	saturday: number;
	sunday: number;
}

export interface IDbAdServerLineItemStatus extends IdName {
	statusKey: string;
	displayName: string;
	siId: number;
}

export interface IDbProposalProbability extends IdName {
	value: number;
}

export class ProcessChartItemDetailDto {
	public lineItemCount?: number;
	public n2Sum?: number;
}

export class ProcessChartItemDto {
	public id: number;
	public processType: string;
	public campaignType: string;
	public campaignNumber: number;
	public campaignVersion: number;
	public title: string;
	public info?: string;
	public details?: ProcessChartItemDetailDto;
	public diffDetails?: ProcessChartItemDetailDto;
	public isRequest: boolean;
	public isCurrent: boolean;
	public arrowTooltip?: string;
}

export class CliPriceListItemDto extends BaseDto {
	public plId: number;
	public plName: string;
	public pliId: number;
	public ptId: number; // only scheduler
	public priceFc: number; // only scheduler
	public priceSc: number; // only scheduler
	public sortId: number;
	public priority: number;
	public unitPriceSc: number;
	public unitPriceFc: number;
	public erAi: number;
}

export class CliPriceListDto extends BaseDto {
	public atId: number;
	public ivId: number;
	public pliId: number;
	public plName: string;
	public ptId: number;
	public sortId: number;
	public priority: number;
	public unitPriceSc: number;
	public unitPriceFc: number;
	public plPtdtlIdSet: number;
	public pliPtdtlId: number;
}
export class ServerPropertyToCampaignDto extends BaseDto {
	public cpgId: number;
	public adserverId: string;
	public status: string;
	public propStatus: string;
	public propLastQueried: Date;
}
export class CampaignRelationDto extends BaseDto {
	public crltId: number;
	public crltName: string;
	public leftCpgId: number;
	public leftCpgName: string;
	public leftCpgNumberVersion: string;
	public rightCpgId: number;
	public rightCpgName: string;
	public rightCpgNumberVersion: string;
}

export class CampaignAdserverLink extends BaseDto {
	public icoId: number;
	public siId: number;
	public adserverId: string;
	public campaignUrl: string;
}

export class KeyValueTargetingBaseDto extends BaseDto {
	public position: number;
	public opcode: number;
	public sysId: number;
	public readOnly: boolean;
	public tkgId: number;
	public level: number;
	public values: KeyValueTargetingDto[];
}

export class KeyValueTargetingDto extends BaseDto {
	public key: number;
	public excluded: boolean;
	public keyDisplayName: string;
	public valueDisplayName: string;
	public valueIds: number[];
	public level: number;
}

export class TreeTargetingRootDto extends BaseDto {
	public name: string;
	public ttlId: number;
	public parentId: number;
	public items: TreeTargetingItemDto[];
}

export class TreeTargetingLevelDto {
	public id: number;
	public symbol: string;
	public ttrId: number;
	public level: number;
	public parentTtlId?: number;
	public name: string;
}

export class TreeTargetingItemDto extends BaseDto {
	public pId: number;
	public fakeId: number;
	public fakeParentId: number;
	public name: string;
	public aId: string;
	public order: number;
	public lId: number;
	public rId: number;
	public include: boolean;
	public exclude: boolean;
	public pii: boolean;
	public pei: boolean;
	public pie: boolean;
	public pee: boolean;
	public lName: string;
	public hc: boolean;
	public siId: number;
	public level: TreeTargetingLevelDto;
}

export class TreeTargetingItemToBaseDto extends BaseDto {
	public ttiId: number;
	public isInclude: boolean;
	public name: string;
	public levelName: string;
	public rootId: number;
	public siId: number;
}

export class CampaignTaskDto extends BaseDto {
	public cttId: number;
	public priority: number;
	public objId: number;
	public creatorUaId: number;
	public created: string;
	public remarkCreated: string;
	public responsibleUaId: number;
	public finished: string;
	public effectiveFrom: string;
	public remarkCompleted: string;
	public isCompleted: boolean;
	public notifyCreator: boolean;
	public pos: number;
}

export class CampaignTaskListDto extends BaseDto {
	public cpgId: number;
	public tasks: CampaignTaskDto[];
}

export interface IDbUserLoginType extends IdName {
	ultId: number;
	ultSymbol: string;
	ultName: string;
	ultIsDefault: boolean;
}

export type IDbAgencyContractApplication = IdName;

export class HierarchicalStructureDto extends BaseDto {
	public name: string;
	public parentId: number;
	public isArchived: boolean;
}

export interface IAudienceSegmentGroups extends IdName {
	items: IAudienceSegmentItem[];
}

export interface IAudienceSegmentItem extends IdName {
	keyName: string;
	isArchived: boolean;
	keyId: number;
}

export class AudienceSegmentToBaseModelDto extends BaseDto {
}

export class SegmentGroupToBaseModelDto extends BaseDto {
	public position: number;
	public segments: IDbSegment[];
	public sysId: number;
	public opId: number;
	public parentId: number;
}

export interface IDbMonitorCampaignDeliveryChartItem {
	seriesId: number;
	seriesName: string;
	deliveryDate: Date;
	units: number;
	unitsPrc: number;
	ai: number;
	aiPrc: number;
	clicks: number;
	clicksPrc: number;
	included: boolean;
}

export interface IDbSegment extends IdName {
	siId: number;
	isArchived: boolean;
	sortId: number;
}

export interface IDbLineItemPerformanceData {
	date: Date;
	tliId: number;
	tsId: number;
	mdtId: number;
	primaryUnits: number;
	primaryAdjust: number;
	ai: number;
	clicks: number;
}

export interface IDbLineItemPerformanceOsiData {
	seriesId: number;
	seriesName: string;
	date: Date;
	day: number;
	targetUnits: number;
	deliveredUnits: number;
	osiPrc: number;
}

export interface IDbDeliveryAnalysisCampaign {
	cpgId: number;
	cpgNumber: number;
	cpgVersion: number;
	cpgName: string;
	cliId: number;
	cliPos: number;
	cliSubpos: number;
	cliPtId: number;
	cliDeliveryFrom: string;
	cliDeliveryTo: string;
	unitName: string;
}

export interface IRateItem {
	id: RateTypeIds;
	name: string;
}

export interface IResolutionItem {
	id: ResolutionTypeIds;
	name: string;
}

export interface IDbQueue {
	quId: number;
	quQgId: number;
	quPriority: number;
	quPostponedCount: number;
	quQpsId: number;
	quClsId: number;
	quObjId: number;
	quFcId: number;
	quCancelable: boolean;
	quInsertedTime: Date;
	quExpProcessDuration: Date;
	quStartProcessTime: Date;
	quEndProcessTime: Date;
	quUaId: number;
}

export interface IQueueStatus {
	qpsId: DbQueueProcessingStatusId;
	qpsName: string;
	qrsId: DbQueueOperationResultStatusId;
	qrsName: string;
	endMessage: string;
}

export class FileColumnDto extends BaseDto {
	public name: string;
	public column: number;
	public row: number;
	public startIndex: number;
	public endIndex: number;
}

export class GridColumnDto extends BaseDto {
	propertyName: string;
	columnName?: string;
	defaultCaption?: string;
	description?: string;
	dataType?: string;
}

export interface IDbTextModule {
	id: number;
	tmName: string;
	tmText: string;
}

export interface IDbCommercialSiteTypes {
	id: number;
	name: string;
	stId: number;
	isRotation: boolean;
}